import React, { useState, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import Loading from "../components/Loading";
import ErrorLog from "../components/ErrorLog";
import useFetchTransactionHistory from '../hooks/useFetchTransactionHistory';
import TransactionHistoryGrid from './TransactionHistoryGrid';

const TransactionHistoryTab = ({ searchInput, handleTransactionStatusByGroupId, messageFlowType, senderName }) => {
  const { transactionHistory, fetchTransactionHistory, historyLoading, historyError } = useFetchTransactionHistory();

  const [dateRangeType, setDateRangeType] = useState("DAY");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleFetchTransactionHistory = async () => {
    if (dateRangeType === "CUSTOM" && (!startDate || !endDate)) {
      toast.error("Please select both start and end dates.");
      return;
    }

    if (dateRangeType === "CUSTOM" && startDate && endDate && startDate > endDate) {
      toast.error("Start date cannot be after end date.");
      return;
    }

    try {
      await fetchTransactionHistory(dateRangeType, startDate, endDate, messageFlowType, senderName); // Include senderName
    } catch (error) {
      toast.error(`Error fetching transaction history: ${error.message}`);
    }
  };

  // Automatically load "Day" date-range when component mounts
  useEffect(() => {
    handleFetchTransactionHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs only on mount

  const filteredTransactions = transactionHistory.filter(transaction =>
    Object.values(transaction).some(value =>
      String(value).toLowerCase().includes(searchInput?.toLowerCase())
    )
  );

  if (historyLoading) {
    return <Loading />;
  }

  if (historyError) {
    return <ErrorLog errorMessage={historyError.message} />;
  }

  return (
    <div>
      <div className="date-range-selector">
        {/* Date Range Radios */}
        <label>
          <input
            type="radio"
            value="DAY"
            checked={dateRangeType === "DAY"}
            onChange={() => setDateRangeType("DAY")}
          />
          Day
        </label>
        <label>
          <input
            type="radio"
            value="WEEK"
            checked={dateRangeType === "WEEK"}
            onChange={() => setDateRangeType("WEEK")}
          />
          Week
        </label>
        <label>
          <input
            type="radio"
            value="MONTH"
            checked={dateRangeType === "MONTH"}
            onChange={() => setDateRangeType("MONTH")}
          />
          Month
        </label>
        <label>
          <input
            type="radio"
            value="CUSTOM"
            checked={dateRangeType === "CUSTOM"}
            onChange={() => setDateRangeType("CUSTOM")}
          />
          Custom
        </label>

        {/* Custom Date Range Picker */}
        {dateRangeType === "CUSTOM" && (
          <div className="custom-date-range">
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              placeholderText="Start Date"
            />
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              placeholderText="End Date"
            />
          </div>
        )}

        {/* Fetch Button */}
        <button
          className="fetch-button"
          style={{
            border: 'none',
            backgroundColor: '#6e70f7de',
            cursor: 'pointer',
            transition: 'background-color 0.3s ease'
          }}
          onClick={handleFetchTransactionHistory}
          disabled={historyLoading || (dateRangeType === "CUSTOM" && (!startDate || !endDate))}
        >
          {historyLoading ? "Loading..." : "Fetch Transaction History"}
        </button>
      </div>

      {/* Render Transaction History */}
      {filteredTransactions.length === 0 ? (
        <div>No transactions available.</div>
      ) : (
        <div className="transaction-history">
          <TransactionHistoryGrid
            transactions={filteredTransactions}
            onTransactionStatus={handleTransactionStatusByGroupId}
          />
        </div>
      )}
    </div>
  );
};

export default TransactionHistoryTab;
